import React from 'react'
import { Card, CardContent, CardHeader, FormInput } from '../ui'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { isLinkableAssetId } from '../../services/informationSystemsApi'

export type BulkAssignAssetRow = {
  id: string
  name: string
  type?: string
  environment?: string
  criticality?: string
  ipAddress?: string
  hostname?: string
  url?: string
}

interface BulkAssignAssetsModalProps {
  open: boolean
  mode: 'link' | 'unlink'
  assets: BulkAssignAssetRow[]
  linkedAssetIds: Set<string>
  onClose: () => void
  onConfirm: (assetIds: string[]) => Promise<{
    linked?: Array<string | number>
    unlinked?: Array<string | number>
    skipped?: Array<string | number>
    errors?: Array<{ assetId: string | number; message: string }>
  }>
}

export default function BulkAssignAssetsModal({
  open,
  mode,
  assets,
  linkedAssetIds,
  onClose,
  onConfirm,
}: BulkAssignAssetsModalProps) {
  const { t } = useI18n()
  const [search, setSearch] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState('')
  const [envFilter, setEnvFilter] = React.useState('')
  const [critFilter, setCritFilter] = React.useState('')
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = React.useState(false)
  const [feedback, setFeedback] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setSelected(new Set())
      setFeedback(null)
      setSearch('')
    }
  }, [open])

  if (!open) return null

  const pool =
    mode === 'link'
      ? assets.filter((a) => isLinkableAssetId(a.id) && !linkedAssetIds.has(String(a.id)))
      : assets.filter((a) => isLinkableAssetId(a.id) && linkedAssetIds.has(String(a.id)))

  const filtered = pool.filter((a) => {
    const q = search.trim().toLowerCase()
    if (q) {
      const hay = [a.name, a.ipAddress, a.hostname, a.url, a.type, a.environment]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!hay.includes(q)) return false
    }
    if (typeFilter && a.type !== typeFilter) return false
    if (envFilter && a.environment !== envFilter) return false
    if (critFilter && (a.criticality || '') !== critFilter) return false
    return true
  })

  const types = [...new Set(pool.map((a) => a.type).filter(Boolean))] as string[]
  const envs = [...new Set(pool.map((a) => a.environment).filter(Boolean))] as string[]
  const crits = [...new Set(pool.map((a) => a.criticality).filter(Boolean))] as string[]

  const toggle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const selectAllVisible = () => setSelected(new Set(filtered.map((a) => String(a.id))))

  const handleSubmit = async () => {
    const ids = [...selected].map((s) => String(s).trim()).filter((id) => isLinkableAssetId(id))
    if (ids.length === 0) return
    setSubmitting(true)
    setFeedback(null)
    try {
      const result = await onConfirm(ids)
      const ok = mode === 'link' ? (result.linked?.length || 0) : (result.unlinked?.length || 0)
      const err = result.errors?.length || 0
      const skip = result.skipped?.length || 0
      setFeedback(`${ok} ${t('assets.bulk.succeeded', 'succeeded')}, ${skip} ${t('assets.bulk.skipped', 'skipped')}, ${err} ${t('assets.bulk.errors', 'errors')}`)
      if (err === 0) onClose()
    } catch (e: unknown) {
      setFeedback(e instanceof Error ? e.message : t('common.error', 'Error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white">
        <CardHeader className="flex justify-between items-center">
          <h3 className="font-semibold text-lg">
            {mode === 'link'
              ? t('assets.bulk.assignTitle', 'Assign assets')
              : t('assets.bulk.unlinkTitle', 'Unlink assets')}
          </h3>
          <Button type="button" color="outline" size="small" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </CardHeader>
        <CardContent className="overflow-y-auto flex-1 space-y-3">
          <div className="grid gap-2 md:grid-cols-4">
            <FormInput
              placeholder={t('assets.bulk.search', 'Search name / IP / hostname / URL')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="border rounded px-2 py-2 text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">{t('assets.bulk.allTypes', 'All types')}</option>
              {types.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
            <select className="border rounded px-2 py-2 text-sm" value={envFilter} onChange={(e) => setEnvFilter(e.target.value)}>
              <option value="">{t('assets.bulk.allEnvironments', 'All environments')}</option>
              {envs.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
            <select className="border rounded px-2 py-2 text-sm" value={critFilter} onChange={(e) => setCritFilter(e.target.value)}>
              <option value="">{t('assets.bulk.allCriticalities', 'All criticalities')}</option>
              {crits.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="small" color="outline" onClick={selectAllVisible}>
              {t('assets.bulk.selectAll', 'Select all visible')}
            </Button>
            <Button type="button" size="small" color="outline" onClick={() => setSelected(new Set())}>
              {t('assets.bulk.clearSelection', 'Clear selection')}
            </Button>
          </div>
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500">{t('assets.bulk.empty', 'No assets match filters.')}</p>
          ) : (
            <ul className="border rounded divide-y max-h-64 overflow-y-auto">
              {filtered.map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(String(a.id))}
                    onChange={() => toggle(String(a.id))}
                  />
                  <span className="flex-1 font-medium">{a.name}</span>
                  <span className="text-gray-500">{a.type || '—'}</span>
                  <span className="text-gray-500">{a.environment || '—'}</span>
                </li>
              ))}
            </ul>
          )}
          {feedback ? <p className="text-sm text-gray-700">{feedback}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" color="secondary" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="button" disabled={submitting || selected.size === 0} onClick={handleSubmit}>
              {submitting ? t('common.saving', 'Saving…') : t('common.confirm', 'Confirm')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
